import { useMemo } from "react";

import clsx from "clsx";

interface Props {
  page: number;
  perPage: number;
  total: number;
  disabled?: boolean;
  onChange: (page: number) => void;
}

export const Paginator = ({
  page,
  perPage,
  total,
  disabled,
  onChange,
}: Props) => {
  const totalPages = Math.ceil(total / perPage);
  const rangeStart = (page - 1) * perPage;
  const rangeEnd = Math.min(rangeStart + perPage, total);

  const pages = useMemo(
    () => Array.from({ length: totalPages }, (_, i) => i + 1),
    [totalPages],
  );

  const visiblePages = useMemo(() => {
    if (pages.length < 6) {
      return pages;
    }

    let from = page - 3;
    let to = page + 2;

    if (from < 0) {
      from = 0;
      to = 5;
    }

    if (to > totalPages) {
      from = totalPages - 5;
      to = totalPages;
    }

    return pages.slice(from, to);
  }, [pages, totalPages, page]);

  return (
    <div className="text-xxs flex items-center gap-4">
      <div className="text-gray">
        Displaying rows {rangeStart + 1}-{rangeEnd} of {total}
      </div>
      <div className="flex items-center gap-1">
        <button
          disabled={disabled}
          className={clsx(
            "h-[25px] min-w-[24px] border border-gray-2 rounded",
            "flex items-center justify-center",
            {
              "text-cosmic bg-white hover:border-0 hover:bg-cosmic-2":
                !disabled,
              "text-gray bg-[#f5f5f5]": disabled,
            },
          )}
          onClick={() => onChange(1)}
        >
          <i className="fa-regular fa-chevrons-left" />
        </button>
        <button
          disabled={disabled}
          className={clsx(
            "h-[25px] min-w-[24px] border border-gray-2 rounded",
            "flex items-center justify-center",
            {
              "text-cosmic bg-white hover:border-0 hover:bg-cosmic-2":
                !disabled,
              "text-gray bg-[#f5f5f5]": disabled,
            },
          )}
          onClick={() => onChange(page > 1 ? page - 1 : page)}
        >
          <i className="fa-regular fa-chevron-left" />
        </button>
        {visiblePages.map((idx) => (
          <button
            disabled={disabled}
            key={idx}
            className={clsx(
              "h-[25px] min-w-[24px] rounded flex items-center justify-center text-xs",
              {
                "font-bold bg-cosmic-3 hover:bg-cosmic-2 text-cosmic":
                  idx !== page && !disabled,
                "font-bold text-white bg-cosmic": idx === page && !disabled,
                "text-gray bg-[#f5f5f5] border border-gray-2": disabled,
              },
            )}
            onClick={() => onChange(idx)}
          >
            {idx}
          </button>
        ))}
        <button
          disabled={disabled}
          className={clsx(
            "h-[25px] min-w-[24px] border border-gray-2 rounded",
            "flex items-center justify-center",
            {
              "text-cosmic bg-white hover:border-0 hover:bg-cosmic-2":
                !disabled,
              "text-gray bg-[#f5f5f5]": disabled,
            },
          )}
          onClick={() => onChange(page < totalPages ? page + 1 : totalPages)}
        >
          <i className="fa-regular fa-chevron-right" />
        </button>
        <button
          disabled={disabled}
          className={clsx(
            "h-[25px] min-w-[24px] border border-gray-2 rounded",
            "flex items-center justify-center",
            {
              "text-cosmic bg-white hover:border-0 hover:bg-cosmic-2":
                !disabled,
              "text-gray bg-[#f5f5f5]": disabled,
            },
          )}
          onClick={() => onChange(totalPages)}
        >
          <i className="fa-regular fa-chevrons-right" />
        </button>
      </div>
    </div>
  );
};
